import React from 'react'
import { Button, Card, CardContent, Input, Label, Modal, RichTextEditor, Badge, Checkbox } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { AlertModal } from '@/shared/ui/AlertModal'
import { listEvolucoes, addEvolucao, updateEvolucao, deleteEvolucao, publishEvolucao, disableEvolucao, type EvolucaoItem } from '../services/evolucoesService'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail } from '@/shared/utils/role'

export default function EvolucoesPage() {
  const { user } = useAuth()
  const canEdit = !!user?.email && isMasterByEmail(user.email)
  const { add: toast } = useToast()

  const [items, setItems] = React.useState<EvolucaoItem[]>([])
  const [loading, setLoading] = React.useState(true)
  const [open, setOpen] = React.useState(false)
  const [title, setTitle] = React.useState('')
  const [html, setHtml] = React.useState('')
  const [effectiveAt, setEffectiveAt] = React.useState<string>('')
  const [createPending, setCreatePending] = React.useState<boolean>(true)
  const [schedule, setSchedule] = React.useState<boolean>(false)
  const [publishAt, setPublishAt] = React.useState<string>('')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const [editOpen, setEditOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<EvolucaoItem | null>(null)
  const [editTitle, setEditTitle] = React.useState('')
  const [editHtml, setEditHtml] = React.useState('')
  const [editEffectiveAt, setEditEffectiveAt] = React.useState<string>('')
  const [editSchedule, setEditSchedule] = React.useState<boolean>(false)
  const [editPublishAt, setEditPublishAt] = React.useState<string>('')
  const [editSaving, setEditSaving] = React.useState(false)
  const [confirmDelete, setConfirmDelete] = React.useState<{ open: boolean; id?: string }>({ open: false })

  async function load() {
    setLoading(true)
    try { setItems(await listSafe()) } finally { setLoading(false) }
  }
  React.useEffect(() => { load() }, [])

  async function onSave() {
    if (!title.trim() || !html.trim()) { setError('Preencha título e conteúdo.'); return }
    setSaving(true); setError(null)
    try {
      const eff = normalizeInputDateTime(effectiveAt)
      const pub = schedule ? normalizeInputDateTime(publishAt) : null
      const status = pub ? 'pending' : (createPending ? 'pending' : 'published')
      await addEvolucao({ title: title.trim(), html, author: user?.email || null, effective_at: eff, publish_at: pub, status })
      toast({ title: 'Nova evolução', description: status==='pending' ? 'Criada como pendente.' : 'Publicada com sucesso.', variant: 'success', soft: true })
      setOpen(false); setTitle(''); setHtml(''); setEffectiveAt(''); setCreatePending(true); setSchedule(false); setPublishAt('')
      await load()
    } catch (e: any) {
      setError(e?.message || 'Falha ao salvar')
      toast({ title: 'Falha ao salvar', description: e?.message || 'Tente novamente', variant: 'error' })
    } finally { setSaving(false) }
  }

  function onStartEdit(it: EvolucaoItem) {
    setEditing(it)
    setEditTitle(it.title)
    setEditHtml(it.html)
    setEditEffectiveAt(toLocalInputDateTime(it.effective_at || it.created_at))
    setEditSchedule(!!it.publish_at)
    setEditPublishAt(toLocalInputDateTime(it.publish_at || null))
    setEditOpen(true)
  }

  async function onSaveEdit() {
    if (!editing) return
    if (!editTitle.trim() || !editHtml.trim()) return
    setEditSaving(true)
    try {
      const eff = normalizeInputDateTime(editEffectiveAt)
      const pub = editSchedule ? normalizeInputDateTime(editPublishAt) : null
      await updateEvolucao(editing.id, { title: editTitle.trim(), html: editHtml, effective_at: eff, publish_at: pub })
      setEditOpen(false); setEditing(null)
      toast({ title: 'Edição salva', description: 'A evolução foi atualizada.', variant: 'success', soft: true })
      await load()
    } catch (e: any) {
      toast({ title: 'Falha ao salvar', description: e?.message || 'Tente novamente', variant: 'error' })
    } finally {
      setEditSaving(false)
    }
  }

  async function onDeleteConfirmed() {
    const id = confirmDelete.id
    setConfirmDelete({ open: false, id: undefined })
    if (!id) return
    try {
      await deleteEvolucao(id)
      toast({ title: 'Excluída', description: 'A evolução foi removida.', variant: 'success', soft: true })
    } catch (e: any) {
      toast({ title: 'Falha ao excluir', description: e?.message || 'Tente novamente', variant: 'error' })
    }
    await load()
  }

  return (
    <div className="mx-auto w-full max-w-[1200px]">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Evoluções</h1>
          <p className="text-sm text-foreground/70">Registro cronológico de melhorias e mudanças.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setOpen(true)}>Adicionar atualização</Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-foreground/70">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-foreground/70">Nenhuma atualização registrada ainda.</div>
      ) : (
        <div className="relative">
          <div className="absolute left-3 top-0 h-full w-[2px] bg-border/70" aria-hidden />
          <div className="space-y-4">
            {items.map((it) => (
              <Card key={it.id}>
                <CardContent className="relative p-4 sm:p-6">
                  <div className="absolute -left-[7px] top-6 h-3 w-3 rounded-full border border-border bg-background" />
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-base font-medium">{it.title}</div>
                    <div className="flex items-center gap-3">
                      <div className="text-xs text-foreground/70">{formatDate(it.effective_at || it.created_at)}</div>
                      {it.status === 'pending' && (
                        <>
                          <Badge variant="soft" color="warning">Pendente</Badge>
                          {it.publish_at && (
                            <span className="text-xs text-foreground/60">Programada: {formatDate(it.publish_at)}</span>
                          )}
                        </>
                      )}
                      {it.status === 'disabled' && (
                        <Badge variant="soft" color="secondary">Desativada</Badge>
                      )}
                      {canEdit && (
                        <div className="flex gap-2">
                          {it.status === 'pending' && (
                            <Button className="h-8 px-3" onClick={async () => {
                              try { await publishEvolucao(it.id); toast({ title: 'Aprovada', description: 'A evolução foi publicada.', variant: 'success', soft: true }) }
                              catch (e:any) { toast({ title: 'Falha ao aprovar', description: e?.message || 'Tente novamente', variant: 'error' }) }
                              await load()
                            }}>Aprovar</Button>
                          )}
                          {it.status === 'published' && (
                            <Button variant="outline" className="h-8 px-3" onClick={async () => {
                              try { await disableEvolucao(it.id); toast({ title: 'Desativada', description: 'A evolução foi ocultada dos membros.', variant: 'info', soft: true }) }
                              catch (e:any) { toast({ title: 'Falha ao desativar', description: e?.message || 'Tente novamente', variant: 'error' }) }
                              await load()
                            }}>Desativar</Button>
                          )}
                          {it.status === 'disabled' && (
                            <Button className="h-8 px-3" onClick={async () => {
                              try { await publishEvolucao(it.id); toast({ title: 'Ativada', description: 'A evolução voltou a ficar visível.', variant: 'success', soft: true }) }
                              catch (e:any) { toast({ title: 'Falha ao ativar', description: e?.message || 'Tente novamente', variant: 'error' }) }
                              await load()
                            }}>Ativar</Button>
                          )}
                          <Button variant="outline" className="h-8 px-3" onClick={() => onStartEdit(it)}>Editar</Button>
                          <Button variant="destructive" className="h-8 px-3" onClick={() => setConfirmDelete({ open: true, id: it.id })}>Excluir</Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="prose prose-invert mt-3 max-w-none text-sm" dangerouslySetInnerHTML={{ __html: it.html }} />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Modal open={open} onOpenChange={(v)=> { if(!v){ setOpen(false) } }} className="p-6">
        <div className="space-y-4">
          <div className="text-lg font-semibold">Nova atualização</div>
          <div className="grid gap-2">
            <Label>Título</Label>
            <Input value={title} onChange={(e)=> setTitle(e.target.value)} placeholder="Ex.: Melhoria na performance do monitor" />
          </div>
          <div className="grid gap-2">
            <Label>Conteúdo</Label>
            <RichTextEditor value={html} onChange={setHtml} height={320} />
          </div>
          <div className="grid gap-2">
            <Label>
              <span className="mr-2">Salvar como pendente</span>
              <Checkbox checked={createPending} onChange={(e: any) => setCreatePending(!!e.target.checked)} />
            </Label>
            <div className="text-xs text-foreground/60">Evoluções pendentes ficam visíveis apenas para usuários master até serem aprovadas.</div>
          </div>
          <div className="grid gap-2">
            <Label>
              <span className="mr-2">Agendar publicação</span>
              <Checkbox checked={schedule} onChange={(e: any) => setSchedule(!!e.target.checked)} />
            </Label>
            <Input type="datetime-local" value={publishAt} onChange={(e)=> setPublishAt(e.target.value)} disabled={!schedule} />
            <div className="text-xs text-foreground/60">Se definido, a evolução será publicada automaticamente no horário agendado.</div>
          </div>
          <div className="grid gap-2">
            <Label>Data (opcional)</Label>
            <Input type="datetime-local" value={effectiveAt} onChange={(e)=> setEffectiveAt(e.target.value)} />
            <div className="text-xs text-foreground/60">Se informado, será usada como data de exibição/ordenação; caso contrário, usamos a data de criação.</div>
          </div>
          {error && <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={()=> setOpen(false)}>Cancelar</Button>
            <Button onClick={onSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </div>
        </div>
      </Modal>

      <Modal open={editOpen} onOpenChange={(v)=> { if(!v){ setEditOpen(false); setEditing(null) } }} className="p-6">
        <div className="space-y-4">
          <div className="text-lg font-semibold">Editar atualização</div>
          <div className="grid gap-2">
            <Label>Título</Label>
            <Input value={editTitle} onChange={(e)=> setEditTitle(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Conteúdo</Label>
            <RichTextEditor value={editHtml} onChange={setEditHtml} height={320} />
          </div>
          <div className="grid gap-2">
            <Label>
              <span className="mr-2">Agendar publicação</span>
              <Checkbox checked={editSchedule} onChange={(e: any) => setEditSchedule(!!e.target.checked)} />
            </Label>
            <Input type="datetime-local" value={editPublishAt} onChange={(e)=> setEditPublishAt(e.target.value)} disabled={!editSchedule} />
          </div>
          <div className="grid gap-2">
            <Label>Data (opcional)</Label>
            <Input type="datetime-local" value={editEffectiveAt} onChange={(e)=> setEditEffectiveAt(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={()=> { setEditOpen(false); setEditing(null) }}>Cancelar</Button>
            <Button onClick={onSaveEdit} disabled={editSaving}>{editSaving ? 'Salvando...' : 'Salvar'}</Button>
          </div>
        </div>
      </Modal>

      <AlertModal
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false })}
        title="Excluir atualização"
        description={<span>Tem certeza que deseja excluir esta atualização? Esta ação não pode ser desfeita.</span>}
        variant="warning"
        confirmText="Excluir"
        onConfirm={onDeleteConfirmed}
      />
    </div>
  )

  async function listSafe(): Promise<EvolucaoItem[]> {
    try { return await listEvolucoes({ includePending: canEdit }) } catch { return [] }
  }
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso)
    const pad = (n: number) => String(n).padStart(2, '0')
    const dd = pad(d.getUTCDate())
    const mm = pad(d.getUTCMonth() + 1)
    const yyyy = d.getUTCFullYear()
    const hh = pad(d.getUTCHours())
    const mi = pad(d.getUTCMinutes())
    const ss = pad(d.getUTCSeconds())
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss} UTC`
  } catch { return iso }
}

function toLocalInputDateTime(iso?: string | null): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    const pad = (n: number) => n.toString().padStart(2, '0')
    const yyyy = d.getFullYear()
    const mm = pad(d.getMonth() + 1)
    const dd = pad(d.getDate())
    const hh = pad(d.getHours())
    const mi = pad(d.getMinutes())
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
  } catch { return '' }
}

function normalizeInputDateTime(val?: string): string | null {
  const v = (val || '').trim()
  if (!v) return null
  try { return new Date(v).toISOString() } catch { return null }
}
